import { useNavigate, useLocation } from "react-router-dom";

/**
 * Options for redirecting to a page.
 */
export interface RedirectOptions {
  /** Query parameters to include in the redirect URL */
  queryParams?: Record<string, string>;

  /** Whether to preserve the origin route in the query params */
  shouldPreserveOrigin?: boolean;

  /** The key to use for the origin route in the query params */
  originParamName?: string;
}

/**
 * Arguments for the redirect function.
 */
export interface RedirectArgs<T extends string> {
  /** The route to redirect to */
  route: T;
  /** Options for the redirect */
  options?: RedirectOptions;
}

/**
 * Type for the redirect function to any app page.
 * @param RouteType The type of route to redirect to.
 */
export type RedirectToPage<T extends string> = (
  args: RedirectArgs<T>
) => void;

/**
 * Generic hook to redirect to any app page with type safety and flexible options.
 * Supports query params, origin preservation, and navigation state.
 */
export function useRedirectToPage<T extends string>(): RedirectToPage<T> {
  const navigate = useNavigate();
  const location = useLocation();

  return ({ route, options = {} }) => {
    const {
      queryParams = {},
      shouldPreserveOrigin = false,
      originParamName = "origin",
    } = options;

    const params = new URLSearchParams(queryParams);

    if (shouldPreserveOrigin) {
      const routeWithOrigin = `${location.pathname}${location.search}`;
      params.append(originParamName, routeWithOrigin);
    }

    navigate({
      pathname: route,
      search: params.toString(),
    });
  };
}
